import Title from "../components/TheTitle.vue";
import { Quill } from "@vueup/vue-quill";
import "quill/dist/quill.snow.css";
import { Validation } from "../utils/validation";
import * as DOMPurify from "dompurify";
import { generateCoupon, getBrandsMaster, getCouponTargetProductsMaster, } from "../api";
import { Authorize } from "../utils/authorize";
export default {
    name: "CouponGeneration",
    components: {
        Title,
    },
    data() {
        return {
            brandInfo: [],
            productInfo: [],
            form: {
                brandId: "",
                couponName: "",
                targetProductId: "",
                discount: "",
                discountUnit: "",
                termOfUse: "",
                expirationDate: "",
                availableTimes: 1,
            },
            // Quill 関連（ref は mounted 時に設定）
            // editorContainer は template の ref を通じてアクセス
            htmlLength: 0,
            textLength: 0, // プレーンテキスト（タグ除去）文字数
        };
    },
    computed: {
        times() {
            return Array.from({ length: 99 }, (_, i) => i + 1);
        },
    },
    methods: {
        updateCounts(container) {
            const editorEl = container.querySelector(".ql-editor");
            if (!editorEl) {
                this.htmlLength = 0;
                this.textLength = 0;
                return;
            }
            // HTML (タグ含む)
            const html = editorEl.innerHTML || "";
            this.htmlLength = html.length;
            // プレーンテキスト（タグを除去）
            const quillInstance = this._quill;
            if (quillInstance) {
                // getText は末尾に改行を含む場合があるため、改行のみの場合は空として扱う
                let plain = (quillInstance.getText() || "").replace(/\n+$/g, "");
                // 空白のみ（スペースやタブ等）のみの場合も長さ0にする
                if (plain.trim().length === 0) {
                    plain = "";
                }
                this.textLength = plain.length;
            }
            else {
                // フォールバック: innerText を使用（同様の処理）
                let plain = (editorEl.innerText || "").replace(/\n+$/g, "");
                if (plain.trim().length === 0) {
                    plain = "";
                }
                this.textLength = plain.length;
            }
        },
        async handleSetting() {
            // エディタ内容を取得
            const editorEl = this.$refs.editorContainer;
            const detailedHtml = editorEl?.querySelector(".ql-editor")?.innerHTML ?? "";
            const param = {
                brand_id: (this.form.brandId || null),
                coupon_name: (this.form.couponName || null),
                target_product_id: (this.form.targetProductId || null),
                detailed_description: (detailedHtml || null),
                discount: (this.form.discount || null),
                discount_unit: (this.form.discountUnit || null),
                term_of_use: (this.form.termOfUse || null),
                expiration_date: (this.form.expirationDate || null),
                available_times: (this.form.availableTimes || null),
                publisher: (this.$store.state.displayName || null),
            };
            const errorMessage = this.validate(param);
            if (errorMessage) {
                this.$store.commit("setMessages", errorMessage);
                this.$store.commit("setCancelBtnHide", true);
                this.$store.commit("setIsModalOpen", true);
                return;
            }
            // リッチテキストのサニタイズ
            const sanitizedText = DOMPurify.default.sanitize(param.detailed_description);
            const response = await generateCoupon(param.brand_id, param.coupon_name, param.target_product_id, sanitizedText, Number(param.discount), Number(param.discount_unit), param.term_of_use, param.expiration_date, param.available_times, param.publisher);
            if (response.result?.code === 9) {
                this.$store.commit("setMessages", response.result.messages);
                this.$store.commit("setCancelBtnHide", true);
                this.$store.commit("setIsModalOpen", true);
            }
            else {
                const couponCode = response.data?.coupon_code;
                this.$router.push(`/couponDetail/${couponCode}`).catch(() => {
                    /* NavigationDuplicated 等のエラーを無視 */
                });
            }
        },
        validate(param) {
            // エラーメッセージを格納する配列
            const errors = [];
            // 必須チェック
            errors.push(...Validation.requiredInput({
                key: "ブランド",
                value: param.brand_id,
            }));
            errors.push(...Validation.requiredInput({
                key: "クーポン名",
                value: param.coupon_name,
            }));
            errors.push(...Validation.requiredInput({
                key: "対象商品",
                value: param.target_product_id,
            }));
            errors.push(...Validation.requiredInput({
                key: "詳細説明（プレーンテキスト）",
                value: this.textLength,
            }));
            errors.push(...Validation.requiredInput({
                key: "割引率・割引額",
                value: param.discount,
            }));
            errors.push(...Validation.requiredInput({
                key: "割引単位",
                value: param.discount_unit,
            }));
            errors.push(...Validation.requiredInput({
                key: "利用条件",
                value: param.term_of_use,
            }));
            errors.push(...Validation.requiredInput({
                key: "有効期限",
                value: param.expiration_date,
            }));
            errors.push(...Validation.requiredInput({
                key: "利用可能回数",
                value: param.available_times,
            }));
            // 桁数チェック
            errors.push(...Validation.digitsMax({
                key: "クーポン名",
                value: param.coupon_name,
            }, 100));
            errors.push(...Validation.digitsMax({
                key: "詳細説明（HTML全体）",
                value: param.detailed_description,
            }, 1000));
            errors.push(...Validation.digitsMax({
                key: "割引額・割引率",
                value: param.discount,
            }, 6));
            errors.push(...Validation.digitsMax({
                key: "利用条件",
                value: param.term_of_use,
            }, 100));
            // 属性チェック
            if (param.discount) {
                errors.push(...Validation.isInteger({
                    key: "割引率・割引額",
                    value: param.discount,
                }));
            }
            if (param.available_times) {
                errors.push(...Validation.isInteger({
                    key: "利用可能回数",
                    value: param.available_times,
                }));
            }
            // 相関チェック
            if (param.expiration_date &&
                new Date() > new Date(param.expiration_date)) {
                errors.push("有効期限に過去の日付は指定できません");
            }
            if (Number(param.discount_unit) === 1 && Number(param.discount) > 100) {
                errors.push("割引単位に％を指定時は、割引率に100以内の値を指定してください。");
            }
            if (errors.length > 0) {
                return errors;
            }
            return "";
        },
    },
    async mounted() {
        if (await Authorize.isExpired(this)) {
            // Quill 初期化
            const container = this.$refs.editorContainer;
            if (!container)
                return;
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore - Quill 型が厳しい場合に無視（必要なら型定義を追加）
            const quill = new Quill(container, {
                theme: "snow",
                modules: {
                    toolbar: [
                        ["bold", "italic", "underline", { color: [] }, "link"],
                        ["clean"],
                    ],
                },
            });
            // 初期カウントを設定
            if (container) {
                this.updateCounts(container);
            }
            // text-change イベントでリアルタイム更新
            quill.on("text-change", () => {
                this.updateCounts(container);
            });
            // Quill インスタンスを this に保持（後で参照したければ）
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            this._quill = quill;
            // ブランドマスタ取得
            const getBrandResponse = await getBrandsMaster();
            if (getBrandResponse.result?.code === 9) {
                this.$store.commit("setMessages", getBrandResponse.result.messages);
                this.$store.commit("setCancelBtnHide", true);
                this.$store.commit("setIsModalOpen", true);
            }
            else {
                this.brandInfo = getBrandResponse.data.results;
            }
            // クーポン対象商品マスタ取得
            const getProductResponse = await getCouponTargetProductsMaster();
            if (getProductResponse.result?.code === 9) {
                this.$store.commit("setMessages", getProductResponse.result.messages);
                this.$store.commit("setCancelBtnHide", true);
                this.$store.commit("setIsModalOpen", true);
            }
            else {
                this.productInfo = getProductResponse.data.results;
            }
        }
    },
    beforeUnmount() {
        // クリーンアップ: 必要に応じて Quill のイベント解除などを行う
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        if (this._quill) {
            // Quill に特別な destroy がないため参照を消す
            // イベントも自前で保持していれば解除する
            this._quill = null;
        }
    },
};
